import { useNavigate, useSearchParams } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { Check, ArrowRight, Loader2, ShieldCheck } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../App";
import { DisclaimerModal } from "../components/DisclaimerModal";
import useSeo from "../hooks/useSeo";
import { PublicHeader } from "../components/PublicHeader";
import { PromoFloatingCard } from "../components/PromoFloatingCard";
import {
  isNativeCapacitor, getAvailablePackages, purchasePackage, restorePurchases,
} from "../lib/revenuecat";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ============================================================================
// Pricing page — public. Mirrors the homepage's cream aesthetic and presents
// two subscription tiers (Monthly + Annual). Each plan starts with a 3-day
// free trial. CTAs route to /signup which will hand off to Stripe Checkout
// once wiring is in place.
// ============================================================================

// Pricing tiers used to power the unified plan card. Both tiers share the
// same highlight list — the user just toggles billing period within one card.
const TIERS = {
  annual: {
    id: "annual",
    badge: "Best value",
    name: "Annual",
    // App Store §3.1.2(c) Apple Jul-2026: the billed total ($69.99/year)
    // MUST be visually dominant over any calculated per-month figure.
    // Headline shows the actual billed amount; the per-month effective
    // cost appears below in smaller, subordinate type.
    aboveSmall: null,
    mainBig: "$69.99",
    mainUnit: "/ year",
    perMonthFootnote: "≈ $5.83 / month, billed once annually",
    subline: "5 days free, then billed annually at $69.99",
    chargedTotal: "$69.99 / year",
  },
  monthly: {
    id: "monthly",
    badge: "Flexible",
    name: "Monthly",
    aboveSmall: null,
    mainBig: "$12.99",
    mainUnit: "/ month",
    perMonthFootnote: null,
    subline: "5 days free, then billed monthly at $12.99",
    chargedTotal: "$12.99 / month",
  },
};
const PLAN_HIGHLIGHTS = [
  "Full library of curated practices",
  "Unlimited bilateral sessions",
  "Real spoken breath cues",
  "Progress tracking + history",
  "Calm Moments weekly recap",
  "Sync across web and mobile",
];

// Single combined plan card. The user toggles between Annual / Monthly with
// a segmented switch; the price + label area updates in place. Two CTAs:
// (1) start the limited 5-day free trial (no charge), or (2) pay now and
// unlock the full library immediately.
const UnifiedPlanCard = ({ selected, onSelect, onStartTrial, onPayNow, ctaLoading, alreadyEntitled, entitlementLabel }) => {
  const tier = TIERS[selected];
  return (
    <div
      className="relative rounded-3xl bg-white p-7 sm:p-9 border-2 shadow-[0_14px_44px_rgba(0,0,0,0.08)]"
      style={{ borderColor: "#3090A8" }}
      data-testid="plan-card"
    >
      <div
        className="absolute -top-3 left-7 px-3 py-1 rounded-full text-xs font-medium text-white tracking-wide"
        style={{ backgroundColor: "#3090A8" }}
      >
        {tier.badge}
      </div>

      {/* Annual / Monthly segmented toggle */}
      <div
        className="bg-slate-100 rounded-full p-1 flex mb-7 mt-4"
        data-testid="plan-toggle"
      >
        {Object.values(TIERS).map((t) => {
          const active = selected === t.id;
          return (
            <button
              key={t.id}
              onClick={() => onSelect(t.id)}
              className={`flex-1 py-2.5 rounded-full text-sm font-medium transition-all ${
                active
                  ? "bg-white shadow-[0_2px_10px_rgba(0,0,0,0.08)] text-[#1B2349]"
                  : "text-[#718096] hover:text-[#1B2349]"
              }`}
              data-testid={`plan-toggle-${t.id}`}
            >
              {t.name}
              {t.id === "annual" && (
                <span
                  className="ml-2 px-1.5 py-0.5 rounded-md text-[10px] font-semibold"
                  style={{ backgroundColor: "#DBEDF1", color: "#3090A8" }}
                >
                  SAVE
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Price block — content updates in place when toggle changes.
          Apple §3.1.2(c): the actual billed amount must be the most
          clear & conspicuous price element. Per-month calculations or
          equivalents appear smaller, below it. */}
      <div className="mb-2 transition-all" key={tier.id}>
        {tier.aboveSmall && (
          <p className="text-sm text-[#3090A8] font-medium mb-1">
            {tier.aboveSmall}
          </p>
        )}
        <div className="flex items-baseline gap-1.5">
          <span
            className="font-outfit font-semibold text-5xl text-[#1B2349] tracking-tight"
            data-testid="plan-billed-total"
          >
            {tier.mainBig}
          </span>
          <span className="text-[#1B2349] text-2xl font-medium">{tier.mainUnit}</span>
        </div>
        {tier.perMonthFootnote && (
          <p
            className="text-xs text-[#718096] mt-1 font-normal"
            data-testid="plan-per-month-footnote"
          >
            {tier.perMonthFootnote}
          </p>
        )}
        <p className="text-sm text-[#718096] mt-1">{tier.subline}</p>
      </div>

      <div className="mt-6 space-y-2.5" data-testid="plan-cta-group">
        {alreadyEntitled && (
          <div
            className="rounded-2xl border border-[#3090A8]/30 bg-[#E1F2F7] p-4 text-center"
            data-testid="plan-already-entitled-notice"
          >
            <p className="text-sm text-[#1B2349] font-medium">
              {entitlementLabel || "You already have full access."}
            </p>
            <p className="text-xs text-[#718096] mt-1">
              Tap a button below to continue to your dashboard.
            </p>
          </div>
        )}
        {/* Path 1 — Start free trial (limited library for 5 days, billed on day 6) */}
        <button
          onClick={() => onStartTrial(selected)}
          disabled={ctaLoading === "trial"}
          className="w-full py-3.5 rounded-full font-medium text-white transition-transform duration-150 active:scale-[0.98] disabled:opacity-70 inline-flex items-center justify-center gap-2"
          style={{ backgroundColor: "#3090A8" }}
          data-testid="plan-start-trial-btn"
        >
          {ctaLoading === "trial" && <Loader2 className="w-4 h-4 animate-spin" />}
          {alreadyEntitled ? "Continue to dashboard" : "Start 5-day free trial"}
        </button>
        <p className="text-center text-xs text-[#718096] -mt-1">
          {alreadyEntitled
            ? "Your subscription is active — no payment required."
            : "Limited library for 5 days · billed on day 6 · cancel anytime"}
        </p>

        {/* Visual divider */}
        <div className="flex items-center gap-3 py-1">
          <div className="flex-1 h-px bg-slate-200" />
          <span className="text-[11px] uppercase tracking-widest text-[#718096] font-medium">or</span>
          <div className="flex-1 h-px bg-slate-200" />
        </div>

        {/* Path 2 — Pay now, full library unlocked immediately */}
        <button
          onClick={() => onPayNow(selected)}
          disabled={ctaLoading === "pay"}
          className="w-full py-3.5 rounded-full font-medium border transition-transform duration-150 active:scale-[0.98] disabled:opacity-70 inline-flex items-center justify-center gap-2"
          style={{ borderColor: "#1B2349", color: "#1B2349", backgroundColor: "#FBF6EE" }}
          data-testid="plan-pay-now-btn"
        >
          {ctaLoading === "pay" && <Loader2 className="w-4 h-4 animate-spin" />}
          {alreadyEntitled ? "Open your dashboard" : "Unlock everything now"}
        </button>
        <p className="text-center text-xs text-[#718096] -mt-1">
          {alreadyEntitled
            ? "Your library is already unlocked — head straight in."
            : `Full library unlocked immediately · ${tier.chargedTotal}`}
        </p>

        {/* App Store §3.1.2(c): functional Terms of Use (EULA) + Privacy
            Policy links must be present in the subscription purchase flow. */}
        <p
          className="text-center text-[11px] text-[#718096] pt-3"
          data-testid="pricing-legal-links"
        >
          By starting a subscription you agree to our{" "}
          <a
            href="/terms"
            className="underline hover:text-[#1B2349]"
            data-testid="pricing-terms-link"
          >
            Terms of Use (EULA)
          </a>{" "}
          and{" "}
          <a
            href="/privacy"
            className="underline hover:text-[#1B2349]"
            data-testid="pricing-privacy-link"
          >
            Privacy Policy
          </a>
          . Subscriptions renew automatically until canceled at least 24 hours
          before the end of the current period. Manage in your Apple ID Account
          Settings.
        </p>

        {/* Trust chip — sits just below the Stripe-bound CTA so it lands
            in the user's eye-line right before the credit-card moment. */}
        <div className="pt-1 flex justify-center">
          <a
            href="/security"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#E1F2F7] text-[#1B2349] text-xs font-medium hover:bg-[#CFEAF1] transition-colors"
            data-testid="pricing-security-chip"
          >
            <ShieldCheck className="w-3.5 h-3.5 text-[#3090A8]" />
            Encrypted &amp; private — read our security promises
          </a>
        </div>
      </div>

      <div className="h-px bg-slate-100 my-7" />

      <ul className="space-y-3">
        {PLAN_HIGHLIGHTS.map((line) => (
          <li key={line} className="flex items-start gap-3">
            <div
              className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
              style={{ backgroundColor: "rgba(48, 144, 168, 0.10)" }}
            >
              <Check className="w-3 h-3" style={{ color: "#3090A8" }} />
            </div>
            <span className="text-[#4A5568]">{line}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const TrialTimelineRow = ({ day, title, body, accent }) => (
  <div className="flex gap-5" data-testid={`pricing-timeline-${day}`}>
    <div className="flex flex-col items-center">
      <div
        className="w-11 h-11 rounded-full flex items-center justify-center text-white font-outfit font-semibold text-sm flex-shrink-0"
        style={{ backgroundColor: accent }}
      >
        {day}
      </div>
      <div className="w-px flex-1 bg-slate-200 mt-2" />
    </div>
    <div className="flex-1 pb-8">
      <p className="text-xs uppercase tracking-widest text-[#3090A8] font-medium mb-1">
        Day {day}
      </p>
      <h4 className="font-outfit font-semibold text-lg text-[#1B2349] mb-1.5">{title}</h4>
      <p className="text-[#4A5568] leading-relaxed">{body}</p>
    </div>
  </div>
);

const FAQItem = ({ q, a }) => (
  <details className="group border-b border-slate-200 py-5" data-testid={`faq-${q.slice(0, 20).toLowerCase().replace(/\s+/g, "-")}`}>
    <summary className="cursor-pointer flex justify-between items-center list-none">
      <span className="font-outfit font-semibold text-lg text-[#1B2349]">{q}</span>
      <ArrowRight className="w-4 h-4 text-[#718096] transition-transform group-open:rotate-90" />
    </summary>
    <div className="mt-3 text-[#4A5568] leading-relaxed">{a}</div>
  </details>
);

const PricingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  useSeo({
    title: "Pricing — Bilateral Calm | EMDR-Inspired Bilateral Sound · 5-Day Free Trial",
    description:
      "Try Bilateral Calm free for 5 days, then $12.99/month or $69.99/year for unlimited bilateral stimulation and bilateral sound practices targeting anxiety, insomnia, stress, and trauma — inspired by EMDR (Eye Movement Desensitization and Reprocessing).",
    path: "/pricing",
  });
  const [searchParams, setSearchParams] = useSearchParams();
  // Initialise `selected` from the URL `?plan=` so refreshes / handoffs
  // from /signup preserve whether the user picked Monthly or Annual. Falls
  // back to "annual" (the default) when no plan param is present.
  const initialPlan = searchParams.get("plan");
  const [selected, setSelected] = useState(
    initialPlan === "monthly" || initialPlan === "annual" ? initialPlan : "annual"
  );
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  // ctaLoading is "trial" | "pay" | null — drives spinner on the right button
  const [ctaLoading, setCtaLoading] = useState(null);
  // Guards the "?autoPay=1" handoff so it only fires once.
  const autoPayFiredRef = useRef(false);

  // Always land users at the top of the membership page when they navigate
  // here from any "Start sign up" / "Subscribe" / "See how the trial works"
  // button across the app.
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, []);

  // Surface the user's existing entitlement so App Review (and any other
  // already-subscribed visitor) sees an explicit "you're in" state instead
  // of tapping a paywall CTA that silently routes them to the dashboard
  // — Apple flagged the silent redirect as a "broken button" bug.
  const entitlementStatus = user?.subscription_status;
  const alreadyEntitled = !!user && (
    entitlementStatus === "waived"
    || entitlementStatus === "active"
    || entitlementStatus === "trialing"
  );
  const entitlementLabel = (() => {
    if (entitlementStatus === "waived") return "Your account already has full, complimentary access.";
    if (entitlementStatus === "active") return "Your subscription is active — full library unlocked.";
    if (entitlementStatus === "trialing") return "You're on the 5-day free trial — full library unlocked.";
    return null;
  })();

  // Path 1: start the limited 5-day free trial.
  // Anonymous → /signup with intent=trial. Authenticated user that already
  // has an entitlement gets a clear toast + visible state on the card
  // (handled by the disabled/relabelled CTA below) instead of a silent
  // navigate.
  const handleStartTrial = (tierId) => {
    if (!user) {
      navigate(`/signup?plan=${tierId}&intent=trial`);
      return;
    }
    if (alreadyEntitled) {
      toast.success(entitlementLabel || "You already have full access.");
      navigate("/dashboard");
      return;
    }
    navigate("/dashboard");
  };

  // Path 2: pay now → full library unlocked immediately.
  // - On iOS (Capacitor): trigger StoreKit via RevenueCat (App Store policy).
  // - On web: anonymous → /signup, authenticated → Stripe Checkout.
  const handlePayNow = async (tierId) => {
    // Already-entitled user: short-circuit with a friendly toast rather
    // than firing a StoreKit purchase that would fail (Apple's reviewer
    // saw this as "An error was displayed after tapping Unlock everything
    // now").
    if (alreadyEntitled) {
      toast.success(entitlementLabel || "You already have full access.");
      navigate("/dashboard");
      return;
    }

    // iOS native shell — App Store §3.1.1 requires StoreKit, not Stripe.
    if (isNativeCapacitor()) {
      if (!user) {
        navigate(`/signup?plan=${tierId}&intent=pay`);
        return;
      }
      setCtaLoading("pay");
      try {
        const packages = await getAvailablePackages();
        // Match RC package by tierId — RC packages identifier conventionally
        // matches the period: "$rc_annual" / "$rc_monthly" or our own slug.
        const target = packages.find((p) => {
          const id = (p?.identifier || "").toLowerCase();
          const prod = (p?.product?.identifier || "").toLowerCase();
          return id.includes(tierId) || prod.includes(tierId);
        }) || packages[0];
        if (!target) {
          toast.error("No subscription products configured yet.");
          return;
        }
        await purchasePackage(target);
        toast.success("Subscription activated.");
        navigate("/dashboard");
      } catch (err) {
        if (!err?.userCancelled) {
          toast.error("Purchase failed. Please try again.");
        }
      } finally {
        setCtaLoading(null);
      }
      return;
    }

    // Web — Stripe Checkout.
    if (!user) {
      navigate(`/signup?plan=${tierId}&intent=pay`);
      return;
    }
    setCtaLoading("pay");
    try {
      const res = await axios.post(
        `${API}/billing/checkout-session`,
        { plan_id: tierId, origin_url: window.location.origin },
        { withCredentials: true }
      );
      if (res.data?.url) {
        window.location.href = res.data.url;
        return;
      }
      toast.error("Could not start checkout. Please try again.");
    } catch (err) {
      // Surface the backend's error reason so silent failures (e.g. stripe
      // misconfig) don't leave the user staring at the pricing page wondering
      // what went wrong.
      const detail = err.response?.data?.detail;
      let msg = "Could not start checkout. Please try again.";
      if (detail === "stripe_not_configured") {
        msg = "Payments aren't configured yet. Please contact support.";
      } else if (detail === "stripe_error") {
        msg = "Stripe couldn't process this request. Please try again in a moment.";
      } else if (detail === "billing_unavailable") {
        msg = "Billing is temporarily unavailable. Please try again later.";
      } else if (detail === "Unknown plan_id") {
        msg = "That plan isn't available. Please refresh and try again.";
      } else if (typeof detail === "string") {
        msg = detail;
      }
      toast.error(msg);
    } finally {
      setCtaLoading(null);
    }
  };

  // App Store guideline §3.1.1 also requires a visible "Restore Purchases"
  // affordance on iOS. Web has nothing to restore (Stripe just keeps billing).
  const handleRestore = async () => {
    setCtaLoading("restore");
    try {
      await restorePurchases();
      toast.success("Purchases restored.");
      navigate("/dashboard");
    } catch (err) {
      toast.error("Nothing to restore.");
    } finally {
      setCtaLoading(null);
    }
  };

  // When a freshly-signed-up user is bounced back to /pricing?autoPay=1 by
  // SignupPage, immediately fire the checkout for the plan they originally
  // selected. Guard with autoPayFiredRef so React StrictMode + URL changes
  // don't double-fire.
  useEffect(() => {
    const autoPay = searchParams.get("autoPay") === "1";
    const plan = searchParams.get("plan");
    if (!autoPay || !user || !plan || autoPayFiredRef.current) return;
    autoPayFiredRef.current = true;
    // Strip the autoPay flag so a refresh doesn't re-trigger.
    const next = new URLSearchParams(searchParams);
    next.delete("autoPay");
    setSearchParams(next, { replace: true });
    handlePayNow(plan);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, searchParams]);

  return (
    <div className="min-h-screen bg-[#FBF8F1]" data-testid="pricing-page">
      <PublicHeader />
      <PromoFloatingCard />

      {/* Hero */}
      <section className="px-6 sm:px-10 pt-10 pb-6 max-w-6xl mx-auto text-center">
        <p className="text-xs uppercase tracking-[0.2em] text-[#3090A8] font-medium mb-5">
          Membership
        </p>
        <h1 className="font-outfit font-semibold text-4xl sm:text-5xl lg:text-6xl text-[#1B2349] tracking-tight mb-5">
          Two ways to begin.
        </h1>
        <p className="text-lg text-[#4A5568] leading-relaxed max-w-2xl mx-auto mb-2">
          Try a starter library free for 5 days, or unlock everything right
          now. Either way you can cancel anytime from your account, and we'll
          always send a gentle reminder before any billing begins.
        </p>
      </section>

      {/* Plan card — single combined card with annual / monthly toggle */}
      <section className="px-6 sm:px-10 py-10 max-w-xl mx-auto">
        <UnifiedPlanCard
          selected={selected}
          onSelect={setSelected}
          onStartTrial={handleStartTrial}
          onPayNow={handlePayNow}
          ctaLoading={ctaLoading}
          alreadyEntitled={alreadyEntitled}
          entitlementLabel={entitlementLabel}
        />
      </section>

      {/* Trial timeline */}
      <section className="px-6 sm:px-10 py-20 bg-[#F4F1E8]/60 border-y border-[#E5DCC9]/50">
        <div className="max-w-3xl mx-auto">
          <p className="text-xs uppercase tracking-[0.2em] text-[#3090A8] font-medium mb-4">
            How the trial works
          </p>
          <h2 className="font-outfit font-semibold text-3xl sm:text-4xl text-[#1B2349] tracking-tight mb-10">
            How the 5-day free trial works.
          </h2>
          <TrialTimelineRow
            day="1"
            title="Full library opens"
            body="You sign up and the full library opens immediately — all 20 guided practices (worry, overwhelm, body tension, headaches, nightmares, grief, sleep, stage fright and more) plus the Daily Calming Practices audio sessions for any moment."
            accent="#3090A8"
          />
          <TrialTimelineRow
            day="4"
            title="Gentle reminder"
            body="We'll remind you just before the trial ends."
            accent="#78C0D8"
          />
          <TrialTimelineRow
            day="5"
            title="Last free day"
            body="Keep practicing. You can cancel from your account at any time — no questions asked."
            accent="#E2B887"
          />
          <div className="flex gap-5">
            <div className="w-11 h-11 rounded-full flex items-center justify-center text-white font-outfit font-semibold text-sm flex-shrink-0 bg-[#1B2349]">
              6
            </div>
            <div className="flex-1">
              <p className="text-xs uppercase tracking-widest text-[#3090A8] font-medium mb-1">Day 6</p>
              <h4 className="font-outfit font-semibold text-lg text-[#1B2349] mb-1.5">
                Full library + your subscription begins
              </h4>
              <p className="text-[#4A5568] leading-relaxed">
                You are billed for the plan you picked, and every practice in
                the library unlocks. Cancel anytime from your account.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="px-6 sm:px-10 py-20 max-w-3xl mx-auto">
        <h2 className="font-outfit font-semibold text-3xl sm:text-4xl text-[#1B2349] tracking-tight mb-8">
          Frequently asked.
        </h2>
        <FAQItem
          q="Will I be charged during the free trial?"
          a="No. We collect your card on signup so the transition is seamless, but your card is not charged until day 6 — and you'll get a reminder before any billing begins. (If you'd rather skip the trial and unlock the full library right away, you can choose 'Unlock everything now' on this page instead.)"
        />
        <FAQItem
          q="How do I cancel?"
          a="You can cancel anytime from the Account section inside the app, or from the link in any reminder email. Canceling during the trial means no charge will ever be made."
        />
        <FAQItem
          q="Can I switch between Monthly and Annual?"
          a="Yes. You can switch plans anytime from your account. When you switch, we prorate the change automatically."
        />
        <FAQItem
          q="Do I need separate subscriptions for web and mobile?"
          a="No — one subscription covers web, iOS, and Android. Your progress, history, and favorites sync across every device you sign in on."
        />
        <FAQItem
          q="Is Bilateral Calm therapy?"
          a={(
            <>
              No. Bilateral Calm is a self-help tool that draws on
              EMDR-informed bilateral stimulation techniques. It is not a
              substitute for professional mental health care. Please refer to
              the{" "}
              <button
                type="button"
                onClick={() => setShowDisclaimer(true)}
                className="text-[#3090A8] font-medium underline underline-offset-2 hover:text-[#1B2349]"
                data-testid="faq-disclaimer-link"
              >
                disclaimer
              </button>
              .
            </>
          )}
        />
      </section>

      {/* iOS-only: Restore Purchases (App Store §3.1.1 requires it). The
          helper hides itself on web by checking the native capacitor flag. */}
      {isNativeCapacitor() && (
        <section className="px-6 sm:px-10 pb-10 max-w-3xl mx-auto text-center">
          <button
            onClick={handleRestore}
            disabled={ctaLoading === "restore"}
            className="text-sm text-[#3090A8] font-medium underline underline-offset-2 hover:text-[#1B2349] disabled:opacity-60"
            data-testid="pricing-restore-btn"
          >
            {ctaLoading === "restore" ? "Restoring…" : "Restore Purchases"}
          </button>
        </section>
      )}

      {/* Footer */}
      <footer className="px-6 sm:px-10 py-10 max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-[#718096]">
        <button onClick={() => navigate("/")} className="flex items-center" data-testid="pricing-footer-home-btn">
          <img src="/logo-bilateral-calm.png" alt="Bilateral Calm" className="h-20 sm:h-24 w-auto" />
        </button>
        <p data-testid="footer-legal">© {new Date().getFullYear()} Mood and Sleep Clinic LLC · Bilateral Calm™ · <a href="mailto:mscwellness@protonmail.com" className="underline underline-offset-2 hover:text-[#1B2349]">mscwellness@protonmail.com</a></p>
      </footer>

      <DisclaimerModal
        open={showDisclaimer}
        onClose={() => setShowDisclaimer(false)}
      />
    </div>
  );
};

export default PricingPage;
